import { formatDate } from "@bw/shared/utils";
import type {
  EntitlementUsageResponse,
  SubscriptionResponse,
  VisitBalanceResponse,
} from "@bw/shared/types/generated";

/**
 * Pure derivation of a subscription's HONEST display state from the server
 * fields. No business logic — this maps the server's `status` + lifecycle flags
 * to a label/tone the UI renders. Every claim is grounded in a real field; where
 * a field is absent (a legacy local-only sub with no period dates) the detail
 * line degrades to null rather than inventing a "renews on".
 *
 * The full state set (from the #1707 lifecycle audit):
 *   - active & !cancel_at_period_end → "Active · Renews {current_period_end}"
 *   - active &  cancel_at_period_end → "Active · Cancels {current_period_end}" (+ reactivate)
 *   - past_due  → "Payment failed · action needed" (no self-serve retry endpoint;
 *                 the CTA points to the facility)
 *   - suspended → "Suspended"
 *   - cancelled → "Canceled" (terminal, no access) + the cancelled_at date
 *   - incomplete→ "Activating" (first charge not yet cleared — never claim Active)
 *   - anything else → the humanized raw status (never a fabricated label)
 *
 * `renews_at` is deliberately IGNORED — it is always null on the current backend;
 * the period boundary is `current_period_end`.
 */

export type SubscriptionTone =
  | "active"
  | "ending"
  | "attention"
  | "suspended"
  | "cancelled";

export interface SubscriptionView {
  tone: SubscriptionTone;
  /** Short status word, e.g. "Active" | "Payment failed" | "Canceled". */
  label: string;
  /** Secondary line, e.g. "Renews Jun 8, 2026" — null when there's nothing honest to say. */
  detail: string | null;
  /** True only for active & cancel_at_period_end — surfaces the Undo/reactivate action. */
  canReactivate: boolean;
  /** True when the member still has facility access (active/ending/past_due). */
  hasAccess: boolean;
  /** True for past_due — the UI points the member at the facility (no self-serve retry). */
  needsFacilityAction: boolean;
}

function humanize(status: string): string {
  const spaced = status.replace(/[_-]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : status;
}

export function deriveSubscriptionView(
  sub: SubscriptionResponse,
): SubscriptionView {
  const periodEnd = sub.current_period_end;
  if (sub.pending_state !== null) {
    const label = {
      activation_pending: "Membership activation pending",
      cancellation_pending: "Membership cancellation pending",
      reactivation_pending: "Membership reactivation pending",
      refund_pending: "Membership refund pending",
    }[sub.pending_state];
    return {
      tone: "attention",
      label,
      detail: null,
      canReactivate: false,
      hasAccess: false,
      needsFacilityAction: false,
    };
  }

  switch (sub.status) {
    case "active":
      if (sub.cancel_at_period_end) {
        return {
          tone: "ending",
          label: "Active",
          detail: periodEnd
            ? `Cancels ${formatDate(periodEnd)}`
            : "Cancels at the end of the period",
          canReactivate: sub.available_actions.includes("reactivate"),
          hasAccess: true,
          needsFacilityAction: false,
        };
      }
      return {
        tone: "active",
        label: "Active",
        // Legacy local-only sub (no Stripe subscription / null period dates) →
        // render gracefully WITHOUT a "renews on" line rather than an em-dash date.
        detail: periodEnd ? `Renews ${formatDate(periodEnd)}` : null,
        canReactivate: false,
        hasAccess: true,
        needsFacilityAction: false,
      };
    case "past_due":
      return {
        tone: "attention",
        label: "Payment failed",
        detail: "Action needed",
        canReactivate: false,
        hasAccess: true,
        needsFacilityAction: true,
      };
    case "suspended":
      return {
        tone: "suspended",
        label: "Suspended",
        detail: null,
        canReactivate: false,
        hasAccess: false,
        needsFacilityAction: false,
      };
    case "cancelled":
      return {
        tone: "cancelled",
        label: "Canceled",
        detail: sub.cancelled_at
          ? `Canceled ${formatDate(sub.cancelled_at)}`
          : null,
        canReactivate: false,
        hasAccess: false,
        needsFacilityAction: false,
      };
    case "incomplete":
      // First charge hasn't cleared — do NOT claim "Active".
      return {
        tone: "attention",
        label: "Activating",
        detail: "Confirming your first payment",
        canReactivate: false,
        hasAccess: false,
        needsFacilityAction: false,
      };
    default:
      // Unknown status — surface the humanized server value, no invented detail.
      return {
        tone: "attention",
        label: humanize(sub.status),
        detail: null,
        canReactivate: false,
        hasAccess: false,
        needsFacilityAction: false,
      };
  }
}

// ---------------------------------------------------------------------------
// Visit balance (visits_card passes)
// ---------------------------------------------------------------------------

export interface VisitBalanceView {
  /** Primary count, e.g. "7 of 10 visits left" | "0 left" | "Expired". */
  label: string;
  /** Expiry line, e.g. "Expires Jun 8, 2026" — null when there's no expiry. */
  expiryDetail: string | null;
  /** True when now >= expires_at — the pass can no longer be redeemed. */
  expired: boolean;
  /** True when remaining_visits <= 0. */
  depleted: boolean;
  /** True when the pass is usable (not expired AND not depleted). */
  usable: boolean;
}

export function deriveVisitBalanceView(
  balance: VisitBalanceResponse,
  now: number = Date.now(),
): VisitBalanceView {
  const expiresAt = balance.expires_at;
  // "now >= expires_at" per the lifecycle audit — an at-the-boundary pass is expired.
  const expiryMs = expiresAt ? new Date(expiresAt).getTime() : null;
  const expired =
    expiryMs !== null && !Number.isNaN(expiryMs) && now >= expiryMs;
  const depleted = balance.remaining_visits <= 0;
  const usable = !expired && !depleted;

  let label: string;
  if (expired) {
    label = "Expired";
  } else if (depleted) {
    label = "0 left";
  } else {
    label = `${balance.remaining_visits} of ${balance.total_visits} visits left`;
  }

  return {
    label,
    expiryDetail:
      expiresAt && !Number.isNaN(expiryMs as number)
        ? `Expires ${formatDate(expiresAt)}`
        : null,
    expired,
    depleted,
    usable,
  };
}

// ---------------------------------------------------------------------------
// Entitlement usage (Slice 6.2 — "N of M included classes used this period")
// ---------------------------------------------------------------------------

export interface EntitlementUsageView {
  /** e.g. "2 of 4 included classes used this month". */
  label: string;
}

/**
 * Pure derivation of the included-classes usage line for a recurring
 * (monthly/quarterly/annual) membership. Returns null when the plan has NO
 * included_sessions entitlement configured for this service (server reports
 * total <= 0) — the row renders nothing rather than a fabricated "0 of 0".
 * `periodNoun` ("month" | "quarter" | "year") comes from planTypeDisplay so
 * the wording matches the plan's real billing cadence; falls back to the
 * generic "period" for an unresolved cadence.
 */
export function deriveEntitlementUsageView(
  usage: EntitlementUsageResponse,
  periodNoun: string | null,
): EntitlementUsageView | null {
  if (usage.total <= 0) return null;
  const noun = periodNoun ?? "period";
  return {
    label: `${usage.used} of ${usage.total} included classes used this ${noun}`,
  };
}
